import { useEffect, useMemo, useState } from 'react'
import {
  CheckCircle2,
  Lock,
  RefreshCw,
  Save,
  Sparkles,
  Trash2,
} from 'lucide-react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { ownerColorsFrom } from '../graphColors'
import {
  STOCK_TYPES,
  type StockType,
  type WorldGraphNode,
  type WorldGraphState,
} from '../types'
import { BuildProgress, LastRunAlert, RunSummary, runFor } from './BuildStatus'
import { useConfirm } from './ConfirmDialog'
import { SETUP_LOCKED } from './ScenarioPhase'
import ChecksPanel from './ChecksPanel'
import NodeTypeLegend from './NodeTypeLegend'

const TARGET_MIN = 30
const TARGET_MAX = 40

interface Props {
  state: WorldGraphState
  anyRunning: boolean
  teamNames: Record<string, string>
  onGenerate: () => Promise<boolean>
  onSave: (nodes: WorldGraphNode[]) => Promise<boolean>
  onApprove: (nodes: WorldGraphNode[]) => Promise<boolean>
  onReopen: () => Promise<boolean>
}

/**
 * The stocks: the 30-40 quantities the world keeps track of. The model
 * proposes them from the approved baseline context; the professor renames,
 * retypes, revalues or removes them, then approves.
 */
export default function StocksPhase({
  state,
  anyRunning,
  teamNames,
  onGenerate,
  onSave,
  onApprove,
  onReopen,
}: Props) {
  const nodes = state.graph.nodes
  const approved = state.stocks_approved
  const build = runFor(state.current_build, 'stocks')
  const running = build?.status === 'running'
  const lastRunFailed =
    build && (build.status === 'failed' || build.status === 'interrupted')
  const [draft, setDraft] = useState<WorldGraphNode[]>(nodes)
  const [busy, setBusy] = useState(false)
  const { confirm, dialog } = useConfirm()
  const ownerColors = useMemo(() => ownerColorsFrom(nodes), [nodes])

  useEffect(() => {
    setDraft(nodes)
  }, [nodes])

  const dirty = JSON.stringify(draft) !== JSON.stringify(nodes)
  const levers = draft.filter((n) => n.type === 'Lever').length
  const drifters = draft.filter((n) => n.type === 'Drifter').length
  const inBand = draft.length >= TARGET_MIN && draft.length <= TARGET_MAX
  const locked = approved || busy || anyRunning

  const run = async (action: () => Promise<boolean>) => {
    setBusy(true)
    const ok = await action()
    setBusy(false)
    return ok
  }

  const update = (id: string, changes: Partial<WorldGraphNode>) =>
    setDraft((current) =>
      current.map((n) => (n.id === id ? { ...n, ...changes } : n))
    )

  const remove = (id: string) =>
    setDraft((current) => current.filter((n) => n.id !== id))

  const regenerate = async () => {
    if (
      nodes.length &&
      !(await confirm({
        title: 'Propose the stocks again?',
        description: 'The new list replaces this one, including your edits.',
        confirmLabel: 'Propose again',
        destructive: true,
      }))
    )
      return
    run(onGenerate)
  }

  const reopen = async () => {
    if (
      (state.links_approved || state.graph.edges.length > 0) &&
      !(await confirm({
        title: 'Re-open the stocks?',
        description:
          'The causal links go back under review, and links to any stock you remove go with it.',
        confirmLabel: 'Re-open',
      }))
    )
      return
    run(onReopen)
  }

  return (
    <Card className='bg-white shadow-md'>
      {dialog}
      <CardHeader>
        <div className='flex items-start justify-between gap-4'>
          <div>
            <CardTitle className='text-lg text-dark-blue'>3. Stocks</CardTitle>
            <CardDescription className='max-w-2xl'>
              The things the world keeps track of, each on a 0–100 scale: what
              teams control, what they want, and what moves on its own. A
              balanced game needs {TARGET_MIN}–{TARGET_MAX}. Players never see
              these numbers.
            </CardDescription>
          </div>
          {approved && (
            <Badge
              variant='outline'
              className='gap-1 border-green-600/30 bg-green-50 text-green-700'
            >
              <Lock className='h-3.5 w-3.5' aria-hidden />
              Approved
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className='space-y-4'>
        {lastRunFailed && <LastRunAlert build={build} />}

        {running && build ? (
          <BuildProgress build={build} />
        ) : !nodes.length ? (
          <div className='rounded-lg border border-dashed border-gray-300 p-6'>
            <div className='flex items-center gap-2 font-medium text-dark-blue'>
              <Sparkles className='h-4 w-4' aria-hidden />
              Propose the stocks
            </div>
            <p className='mt-1 text-sm text-muted-foreground'>
              The model reads the approved baseline context and proposes the
              stocks, asking again if the first list comes up short.
            </p>
            <Button
              className='mt-4 bg-dark-blue hover:bg-dark-blue-hovered'
              disabled={busy || anyRunning}
              onClick={() => run(onGenerate)}
            >
              <Sparkles className='h-4 w-4' aria-hidden />
              {lastRunFailed ? 'Start again' : 'Propose stocks'}
            </Button>
          </div>
        ) : (
          <>
            <div
              className='flex flex-wrap items-center gap-2 text-sm'
              data-testid='world-graph-stock-summary'
            >
              <span className='font-medium text-dark-blue'>
                {draft.length} stocks
              </span>
              <span className='text-muted-foreground'>
                · {levers} levers · {drifters} drifting
              </span>
              <Badge
                variant='outline'
                className={cn(
                  'px-2 py-0.5 text-xs font-medium',
                  inBand
                    ? 'border-green-600/30 bg-green-50 text-green-700'
                    : 'border-amber-500/40 bg-amber-50 text-amber-800'
                )}
              >
                {inBand
                  ? `In the ${TARGET_MIN}–${TARGET_MAX} range`
                  : `Outside ${TARGET_MIN}–${TARGET_MAX}`}
              </Badge>
            </div>

            {build?.status === 'succeeded' && (
              <RunSummary build={build} verb='Proposed' />
            )}
            {!state.graph.edges.length && (
              <ChecksPanel
                errors={state.checks.errors}
                warnings={state.checks.warnings}
              />
            )}
            <NodeTypeLegend />

            <div className='overflow-x-auto rounded-lg border'>
              <table
                className='w-full text-sm'
                data-testid='world-graph-stocks'
              >
                <thead className='bg-muted/50 text-left text-xs uppercase text-muted-foreground'>
                  <tr>
                    <th className='px-3 py-2'>Stock</th>
                    <th className='px-3 py-2'>Owner</th>
                    <th className='px-3 py-2'>Category</th>
                    <th className='px-3 py-2'>Type</th>
                    <th className='px-3 py-2'>Starts at</th>
                    <th
                      className='px-3 py-2'
                      title='A low number is the good outcome'
                    >
                      Low is good
                    </th>
                    {!approved && <th className='px-3 py-2' />}
                  </tr>
                </thead>
                <tbody>
                  {draft.map((node) => {
                    const ownerColor = node.owner
                      ? ownerColors.get(node.owner)
                      : null
                    return (
                      <tr key={node.id} className='border-t align-top'>
                        <td className='min-w-[220px] px-3 py-2'>
                          <Input
                            aria-label={`Name of ${node.id}`}
                            value={node.label}
                            disabled={locked}
                            onChange={(e) =>
                              update(node.id, { label: e.target.value })
                            }
                            className='h-8'
                          />
                          {node.measures && (
                            <p className='mt-1 text-xs text-muted-foreground'>
                              {node.measures}
                            </p>
                          )}
                        </td>
                        <td className='whitespace-nowrap px-3 py-2'>
                          <span
                            className='inline-flex items-center gap-1.5'
                            style={
                              ownerColor ? { color: ownerColor } : undefined
                            }
                          >
                            {ownerColor && (
                              <span
                                className='h-2 w-2 rounded-full'
                                style={{ backgroundColor: ownerColor }}
                              />
                            )}
                            {node.owner
                              ? teamNames[node.owner] || node.owner
                              : 'Shared'}
                          </span>
                        </td>
                        <td className='px-3 py-2'>
                          <Input
                            aria-label={`Category of ${node.id}`}
                            value={node.category}
                            disabled={locked}
                            onChange={(e) =>
                              update(node.id, { category: e.target.value })
                            }
                            className='h-8 w-32'
                          />
                        </td>
                        <td className='px-3 py-2'>
                          <select
                            aria-label={`Type of ${node.id}`}
                            value={node.type}
                            disabled={locked}
                            onChange={(e) =>
                              update(node.id, {
                                type: e.target.value as StockType,
                              })
                            }
                            className='h-8 rounded-md border border-input bg-background px-2 text-sm'
                          >
                            {STOCK_TYPES.map((type) => (
                              <option key={type} value={type}>
                                {type}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className='px-3 py-2'>
                          <Input
                            aria-label={`Starting value of ${node.id}`}
                            type='number'
                            min={0}
                            max={100}
                            value={node.value}
                            disabled={locked}
                            onChange={(e) =>
                              update(node.id, { value: Number(e.target.value) })
                            }
                            className='h-8 w-20'
                          />
                        </td>
                        <td className='px-3 py-2 text-center'>
                          <input
                            type='checkbox'
                            aria-label={`Low is good for ${node.id}`}
                            checked={node.inverted}
                            disabled={locked}
                            onChange={(e) =>
                              update(node.id, { inverted: e.target.checked })
                            }
                          />
                        </td>
                        {!approved && (
                          <td className='px-3 py-2'>
                            <Button
                              type='button'
                              variant='ghost'
                              size='icon'
                              className='h-8 w-8 text-muted-foreground hover:text-red-700'
                              aria-label={`Remove ${node.label}`}
                              disabled={locked}
                              onClick={() => remove(node.id)}
                            >
                              <Trash2 className='h-4 w-4' />
                            </Button>
                          </td>
                        )}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            <div className='flex flex-wrap justify-end gap-2'>
              {approved && state.setup_locked ? (
                <p className='text-sm text-muted-foreground'>{SETUP_LOCKED}</p>
              ) : approved ? (
                <Button
                  variant='outline'
                  disabled={busy || anyRunning}
                  onClick={reopen}
                >
                  Re-open to edit
                </Button>
              ) : (
                <>
                  <Button
                    variant='outline'
                    disabled={locked}
                    onClick={regenerate}
                  >
                    <RefreshCw className='h-4 w-4' aria-hidden />
                    Propose again
                  </Button>
                  <Button
                    variant='outline'
                    disabled={locked || !dirty}
                    onClick={() => run(() => onSave(draft))}
                  >
                    <Save className='h-4 w-4' aria-hidden />
                    Save changes
                  </Button>
                  <Button
                    className='bg-dark-blue hover:bg-dark-blue-hovered'
                    disabled={locked || !draft.length}
                    onClick={() => run(() => onApprove(draft))}
                  >
                    <CheckCircle2 className='h-4 w-4' aria-hidden />
                    Approve stocks
                  </Button>
                </>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}
